import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Circle, Plus, X } from 'lucide-react';
import { format, parseISO, startOfDay } from 'date-fns';
import { useBaby } from '@/contexts/BabyContext';
import {
  getMedications,
  getMedicineLog,
  logMedicineDose,
  unlogMedicineDose,
} from '@/lib/health-firestore';
import type { Medication, MedicationTiming } from '@/types';
import AddMedicineModal from '@/components/health/AddMedicineModal';

const TIMING_LABELS: Record<MedicationTiming, string> = {
  morning:          'Morning',
  afternoon:        'Afternoon',
  evening:          'Evening',
  night:            'Night',
  after_breakfast:  'After Breakfast',
  after_lunch:      'After Lunch',
  after_dinner:     'After Dinner',
  before_breakfast: 'Before Breakfast',
  before_lunch:     'Before Lunch',
  before_dinner:    'Before Dinner',
};

function isMedicationActiveToday(med: Medication): boolean {
  const today = startOfDay(new Date());
  const start = startOfDay(parseISO(med.startDate));
  if (start > today) return false;
  if (med.endDate) {
    const end = startOfDay(parseISO(med.endDate));
    return today <= end;
  }
  return true;
}

interface MedicineDoseSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const MedicineDoseSheet = ({ isOpen, onClose }: MedicineDoseSheetProps) => {
  const { activeBaby } = useBaby();
  const [medications, setMedications] = useState<Medication[]>([]);
  const [todayLog, setTodayLog] = useState<Record<string, { done: boolean; timestamp: string }>>({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const loadData = useCallback(async () => {
    if (!activeBaby?.id) return;
    setLoading(true);
    try {
      const [meds, log] = await Promise.all([
        getMedications(activeBaby.id),
        getMedicineLog(activeBaby.id),
      ]);
      setMedications(meds);
      setTodayLog(log);
    } finally {
      setLoading(false);
    }
  }, [activeBaby?.id]);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  const handleTick = async (med: Medication, timing: MedicationTiming) => {
    if (!activeBaby?.id) return;
    const key = `${med.id}_${timing}`;
    const isDone = todayLog[key]?.done;
    const newLog = { ...todayLog, [key]: { done: !isDone, timestamp: new Date().toISOString() } };
    setTodayLog(newLog);

    // Auto-close if all doses are now done (only when marking as done, not unticking)
    if (!isDone) {
      const activeMeds = medications.filter(m => m.isActive && isMedicationActiveToday(m));
      const allDone = activeMeds.every(m =>
        m.timings.every(t => newLog[`${m.id}_${t}`]?.done)
      );
      if (allDone) setTimeout(onClose, 400);
    }

    try {
      if (isDone) {
        await unlogMedicineDose(activeBaby.id, med.id, timing);
      } else {
        await logMedicineDose(activeBaby.id, med.id, timing);
      }
    } catch {
      setTodayLog(prev => ({
        ...prev,
        [key]: { done: isDone ?? false, timestamp: '' },
      }));
    }
  };

  if (!isOpen) return null;

  const activeMeds = medications.filter(m => m.isActive && isMedicationActiveToday(m));

  return (
    <>
      <div className="fixed inset-0 z-[55] bg-black/40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white rounded-t-3xl pb-8 max-w-lg mx-auto max-h-[85vh] flex flex-col" style={{ boxShadow: 'var(--shadow-modal)' }}>
        {/* Handle */}
        <div className="w-10 h-1 rounded-full bg-muted mx-auto mt-4 mb-1 flex-shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0">
          <div>
            <p className="text-base font-bold text-foreground">Today's Medicines</p>
            <p className="text-xs text-muted-foreground">{format(new Date(), 'd MMM yyyy')}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-2">
          {loading ? (
            <div className="space-y-3 py-4">
              {[1, 2].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}
            </div>
          ) : activeMeds.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-muted-foreground">No active medicines today</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 text-sm font-semibold text-primary"
              >
                + Add a medicine
              </button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {activeMeds.map(med => (
                <div key={med.id} className="bg-muted/30 rounded-2xl overflow-hidden">
                  {/* Medicine name */}
                  <div className="px-4 pt-3 pb-2">
                    <p className="text-sm font-semibold text-foreground">{med.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {med.dose} {med.unit}
                      {med.endDate ? ` · Until ${format(parseISO(med.endDate), 'd MMM')}` : ' · Ongoing'}
                    </p>
                  </div>

                  {/* Dose checkboxes */}
                  <div className="px-3 pb-3 space-y-2">
                    {med.timings.map(timing => {
                      const key = `${med.id}_${timing}`;
                      const done = todayLog[key]?.done;
                      const doneTime = done && todayLog[key]?.timestamp
                        ? format(new Date(todayLog[key].timestamp), 'h:mm a')
                        : null;
                      return (
                        <button
                          key={timing}
                          onClick={() => handleTick(med, timing)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors"
                          style={done
                            ? { background: 'hsl(150,50%,93%)', border: '1px solid hsl(150,50%,55%)' }
                            : { background: 'white', border: '1px solid hsl(var(--border))' }}
                        >
                          {done
                            ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(150,50%,45%)' }} />
                            : <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          }
                          <span className={`text-sm flex-1 ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {TIMING_LABELS[timing]}
                          </span>
                          {doneTime && (
                            <span className="text-xs" style={{ color: 'hsl(150,50%,45%)' }}>{doneTime}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add medicine link */}
        <div className="px-5 pt-3 pb-2 border-t border-border flex-shrink-0">
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-border text-sm text-muted-foreground"
          >
            <Plus className="w-4 h-4" />
            Add medicine
          </button>
        </div>
      </div>

      {showAddModal && activeBaby && (
        <AddMedicineModal
          babyId={activeBaby.id}
          onSaved={() => { setShowAddModal(false); loadData(); }}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </>
  );
};

export default MedicineDoseSheet;
